import styles from './Popover.module.scss';

import ChevronRight from '@mui/icons-material/ChevronRight';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Column, Table } from '@tanstack/react-table';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SELECTION_COLUMN_ID } from '@work4all/components/lib/dataDisplay/basic-table/utils/makeRowsSelectable';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';
import { NavigationPopover } from '@work4all/components/lib/navigation/navigation-popover';

import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import {
  ERP_EXPAND_COLUMN_ID,
  OBJECT_LOCK_COLUMN_ID,
} from '../../../../../generic-column/GenericColumns';

interface IPopoverV8Props {
  popoverState: ReturnType<typeof usePopoverState>;
  table: Table<unknown>;
  subgroupPath?: string[];
}

const getLeavesAndTwigs = (
  columns: Array<Column<unknown> & { title?: string }>,
  currentPath: string[]
): { leaves: Array<Column<unknown> & { title?: string }>; twigs: string[] } => {
  const result = { leaves: [], twigs: [] };

  const currentPathString = currentPath.join('.');

  columns.forEach((col) => {
    const subGroupPathString = col.columnDef.filterSubgroupPath
      ? col.columnDef.filterSubgroupPath.join('.')
      : '';

    if (subGroupPathString === currentPathString) {
      result.leaves.push(col);
    } else if (
      subGroupPathString.startsWith(currentPathString) &&
      col.columnDef.filterSubgroupPath.length - currentPath.length === 1 &&
      result.twigs.indexOf(
        col.columnDef.filterSubgroupPath[currentPath.length]
      ) === -1
    ) {
      result.twigs.push(col.columnDef.filterSubgroupPath[currentPath.length]);
    }
  });

  return result;
};

const getList = (col: Column<unknown>) => {
  return {
    // HACK Using `col.isSorted` here doesn't return the correct value and
    // some columns are displayed as sorted when they are not and the other
    // way around.

    // EDIT Most likely no longer relevant after upgrading to v8

    // TODO Investigate the cause of this issue. The value `isSorted`
    // property is not correct and does not match the actual `sortBy` value
    // in the table state. Sometimes there is only one column in the state,
    // but two or more columns have `isSorted` set to `true`. Sometimes none
    // of the columns have `isSorted` set to true, even though the table is
    // sorted and there is a column in the `sortBy` state. This only happens
    // in "card" list layout and I want's able to reproduce it with regular
    // "table" layout.

    checkedVal: col.getIsSorted(),
    // checkedVal: !!props.tableInstanceRef.current?.state.sortBy.find(
    //   (c) => c.id === col.id
    // ),
    onChangeFunc: () => {
      col.toggleSorting(undefined, false);
    },
  };
};

export const VISIBILITY_COLUMNS_EXCEPTIONS = [
  ERP_EXPAND_COLUMN_ID,
  SELECTION_COLUMN_ID,
  OBJECT_LOCK_COLUMN_ID,
];

const PopoverV8SubMenu: React.FC<IPopoverV8Props> = (props) => {
  const { subgroupPath, table, popoverState } = props;

  const { t } = useTranslation();

  const { go } = useHistoryStack();

  const goToSubgroup = (subgroupPath: string[]) => {
    go({
      title: t(subgroupPath[subgroupPath.length - 1]),

      view: (
        <PopoverV8SubMenu
          popoverState={popoverState}
          table={table}
          subgroupPath={subgroupPath}
        />
      ),
    });
  };

  const columns = table.getAllFlatColumns();

  const leavesAndTwigs = useMemo(() => {
    return getLeavesAndTwigs(
      columns.filter((col) => {
        return (
          !VISIBILITY_COLUMNS_EXCEPTIONS.includes(col.id) && col.getCanSort()
        );
      }),
      subgroupPath
    );
  }, [columns, subgroupPath]);

  return (
    <List disablePadding>
      {leavesAndTwigs.leaves
        .sort((a, b) => {
          // Keep the original sort order for custom fields.
          if (isEqual(subgroupPath, ['MASK.INDIVIDUAL'])) {
            return (a.columnDef.order ?? 0) - (b.columnDef.order ?? 0);
          }

          return (a.columnDef.title ?? '').localeCompare(
            b.columnDef.title ?? ''
          );
        })
        .map((col) => {
          const list = getList(col);

          return (
            <ListItem key={col.id} disablePadding>
              <ListItemButton role={undefined} onClick={list.onChangeFunc}>
                <ListItemIcon>
                  {list.checkedVal ? (
                    <RadioButtonCheckedIcon color="primary" />
                  ) : (
                    <RadioButtonUncheckedIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    typeof col.columnDef.header === 'string'
                      ? col.columnDef.header
                      : typeof col.columnDef.title === 'string'
                      ? col.title
                      : col.id
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}

      {leavesAndTwigs?.twigs?.length > 0 && (
        <Divider className={styles.divider} />
      )}

      {leavesAndTwigs.twigs
        .sort((a, b) => {
          // Custom fields must always be displayed as the last option.
          if (a === 'MASK.INDIVIDUAL') return 1;
          if (b === 'MASK.INDIVIDUAL') return -1;

          return t(a) > t(b) ? 1 : t(a) === t(b) ? 0 : -1;
        })
        .map((subgroup) => {
          return (
            <ListItem key={subgroup} disablePadding>
              <ListItemButton
                onClick={() => {
                  goToSubgroup([...subgroupPath, subgroup]);
                }}
              >
                <ListItemText
                  className={styles.itemText}
                  classes={{ primary: styles.primary }}
                >
                  {t(subgroup)}
                </ListItemText>

                <ChevronRight className={styles.itemIcon} />
              </ListItemButton>
            </ListItem>
          );
        })}
    </List>
  );
};

// TODO This Popover component is in for an overhaul. It seems it was designed
// to be used for both "column visibility" and "grouping" popovers but now there
// is a separate component for "grouping" popover. So this one needs to be
// stripped of all unnecessary logic.
export const PopoverV8: React.FC<IPopoverV8Props> = (props) => {
  const { table, popoverState } = props;

  //if the initial view changes while the popover is open, then the stack resets
  const [initalView, setInitialView] = useState({ title: '', view: null });

  const { t } = useTranslation();

  useEffect(() => {
    if (popoverState.open) return;
    setInitialView({
      title: t('COMMON.SORT'),
      view: (
        <PopoverV8SubMenu
          table={table}
          popoverState={popoverState}
          subgroupPath={[]}
        />
      ),
    });
  }, [popoverState, t]);

  return (
    <NavigationPopover
      anchorEl={props.popoverState.anchorEl}
      onClose={props.popoverState.onClose}
      open={Boolean(props.popoverState.anchorEl)}
      initialView={initalView}
    />
  );
};
